"use client"

import { Button } from "@/components/ui/button"
import { Trash2, Edit2, Save, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

interface PropertyActionsProps {
    isEditing: boolean
    isUpdating: boolean
    isDeleting: boolean
    onEdit: () => void
    onSave: () => void
    onCancel: () => void
    onDelete: () => void
}

export const PropertyActions = ({
    isEditing,
    isUpdating,
    isDeleting,
    onEdit,
    onSave,
    onCancel,
    onDelete,
}: PropertyActionsProps) => {
    const { t } = useTranslation()

    return (
        <div className="flex items-center gap-2">
            <TooltipProvider>
                <AlertDialog>
                    <Tooltip>
                        <AlertDialogTrigger asChild>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    disabled={isDeleting}
                                >
                                    <Trash2 className="h-4 w-4" />
                                    <span className="sr-only">{t("common.delete")}</span>
                                </Button>
                            </TooltipTrigger>
                        </AlertDialogTrigger>
                        <TooltipContent>
                            {isDeleting ? t("common.deleting") : t("common.delete")}
                        </TooltipContent>
                    </Tooltip>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>
                                {t("properties.confirmations.delete.title")}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                                {t("properties.confirmations.delete.description")}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                            <AlertDialogAction onClick={onDelete}>
                                {t("common.delete")}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {isEditing ? (
                    <>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={onCancel}
                                    disabled={isUpdating}
                                >
                                    <X className="h-4 w-4" />
                                    <span className="sr-only">{t("common.cancel")}</span>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t("common.cancel")}</TooltipContent>
                        </Tooltip>

                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={onSave}
                                    disabled={isUpdating}
                                >
                                    <Save className="h-4 w-4" />
                                    <span className="sr-only">{t("common.save")}</span>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                {isUpdating ? t("common.saving") : t("common.save")}
                            </TooltipContent>
                        </Tooltip>
                    </>
                ) : (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={onEdit}
                                disabled={isUpdating}
                            >
                                <Edit2 className="h-4 w-4" />
                                <span className="sr-only">{t("common.edit")}</span>
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t("common.edit")}</TooltipContent>
                    </Tooltip>
                )}
            </TooltipProvider>
        </div>
    )
}

